/**
 * useSubscriptionModal — opens payment/subscription modals hosted in scio1 iframe.
 *
 * Three modal types:
 * 1. openAddOnRequired — add-on license limit (AI, Sensor, Video Wall, Background Music)
 * 2. openSubscriptionPlan — plan upgrade needed (e.g., Pro Plus+ for Remote Control)
 * 3. openRequiredPlan — specific plan required (e.g., Engage for interactive assets)
 *
 * Uses Scio1Frame mode="modal" (hidden iframe) for postMessage bridge.
 * When embedded in scio1, falls back to iframe-child-bridge.
 *
 * @see claude-dev/2026-02-09-screen-ted/final-document/fundamentals/payment-modal.md
 */
import { useCallback } from 'react';
import { useNavigate } from 'react-router';
import { DEVICE_CODE_STATUS } from '@scio3/shared-types';
import { useAuthStore } from '@/stores/auth-store';
import { useScio1ModalStore } from '@/stores/scio1-modal-store';
import { isInIframeContext, sendToParentWithPromise } from '@/lib/iframe-child-bridge';
import {
  useBillingDialogStore,
  openUpgradeWizard,
} from '@/features/billing/stores/billing-dialog-store';

const useNativeBilling = import.meta.env.VITE_USE_NATIVE_BILLING === 'true';

// --- Response types from scio1 modals ---

export interface AddOnRequiredResponse {
  status?: boolean;
  type?: 'goToSub';
  close?: boolean;
}

export interface SubscriptionPlanResponse {
  type?: 'goToSub';
  [key: string]: unknown;
}

// --- Error extraction ---

interface GraphQLErrorExtension {
  status?: boolean;
  items?: unknown[];
  [key: string]: unknown;
}

/**
 * Get the first GraphQL error from an Apollo error.
 * Apollo Client 4 CombinedGraphQLErrors uses `errors`, not `graphQLErrors`.
 * We check both for compatibility.
 */
function getFirstGqlError(error: unknown):
  | {
      extensions?: Record<string, unknown>;
      message?: string;
    }
  | undefined {
  const err = error as {
    graphQLErrors?: Array<{ extensions?: Record<string, unknown>; message?: string }>;
    errors?: Array<{ extensions?: Record<string, unknown>; message?: string }>;
  };
  return err?.graphQLErrors?.[0] ?? err?.errors?.[0];
}

/**
 * Extract error extension from a GraphQL error for plan/addon gating detection.
 *
 * NestJS HttpExceptions wrap error data in extensions.exception.response.
 * Always prefer the nested response when available — it contains the clean data shape
 * (status, isCompanyTrial, haveCard, maxQuantity, etc.) that scio1 modals expect.
 * This matches scio2's getExtensionGraphQL: error.graphQLErrors[0].extensions.exception.response
 *
 * Returns the extension data if it's a plan/addon error, null otherwise.
 */
export function getGraphQLErrorExtension(error: unknown): GraphQLErrorExtension | null {
  const gqlError = getFirstGqlError(error);
  if (!gqlError) return null;

  const ext = gqlError.extensions;
  if (!ext) return null;

  // NestJS HttpException wraps error data in extensions.exception.response
  // Prefer nested response (clean data shape for scio1 modals), fall back to top-level extensions
  const exception = ext.exception as Record<string, unknown> | undefined;
  const nestedResponse = exception?.response as Record<string, unknown> | undefined;

  // Check top-level code for explicitly coded errors
  const code = ext.code as string | undefined;
  if (
    code === 'REQUIRED_PLAN' ||
    code === 'ADDON_ENABLE_TRIAL_NOT_ENOUGH' ||
    code === 'ADDON_ENABLE_PAID_NOT_ENOUGH'
  ) {
    return (nestedResponse ?? ext) as GraphQLErrorExtension;
  }

  // Check nested exception.response for NestJS HttpException errors (ADDON_NOT_ENOUGH, etc.)
  // Backend throws NotAcceptableException → Apollo wraps as INTERNAL_SERVER_ERROR
  if (nestedResponse && nestedResponse.status === false) {
    return nestedResponse as GraphQLErrorExtension;
  }

  return null;
}

/**
 * Extract the error code from a GraphQL error.
 */
export function getGraphQLErrorCode(error: unknown): string | undefined {
  const gqlError = getFirstGqlError(error);
  return gqlError?.extensions?.code as string | undefined;
}

export function useSubscriptionModal() {
  const navigate = useNavigate();
  const isEmbedded = useAuthStore((s) => s.isEmbedded);

  const sendToScio1 = useCallback(
    async <T = unknown>(event: string, data: unknown): Promise<T> => {
      // When embedded inside scio1, use parent bridge
      if (isEmbedded && isInIframeContext) {
        return sendToParentWithPromise<T>(event, data, 300000);
      }

      // Normal mode: use the adapter iframe.
      // Wait up to 45s for the bridge to register — the modal adapter loads adapterPortalSecond
      // from scio1 via DDP, which can take 10-30s on first load. Without this wait, clicking
      // Upgrade immediately after login silently does nothing (sendWithPromise is still null).
      const waitForBridge = (): Promise<void> =>
        new Promise((resolve, reject) => {
          if (useScio1ModalStore.getState().sendWithPromise) {
            resolve();
            return;
          }
          const timeout = setTimeout(() => {
            unsubscribe();
            reject(new Error('scio1 modal adapter not ready after 45s'));
          }, 45_000);
          const unsubscribe = useScio1ModalStore.subscribe((state) => {
            if (state.sendWithPromise) {
              clearTimeout(timeout);
              unsubscribe();
              resolve();
            }
          });
        });

      await waitForBridge();
      const { sendWithPromise, postToIframe, showModal } = useScio1ModalStore.getState();

      // Ensure the iframe is on the adapter page before opening any modal.
      // scio1 may have navigated away; this mirrors scio2's routerRedirect({ url: METEOR_MAIN_ADAPTER_URL }).
      postToIframe?.({
        event: 'ROUTER_REDIRECT',
        data: { url: 'app.adapterPortalSecond' },
      });

      showModal();
      try {
        // sendWithPromise is guaranteed non-null here — waitForBridge() resolved only after it was set
        if (!sendWithPromise) throw new Error('sendWithPromise unavailable after bridge init');
        const result = await sendWithPromise<T>(event, data);
        return result;
      } finally {
        useScio1ModalStore.getState().hideModal();
      }
    },
    [isEmbedded],
  );

  /**
   * Open the Add-On Required modal in scio1.
   * Called when addWidgetAddonDevices / toggleDeviceFeature fails with addon license errors.
   * Backend error extension may already contain an `items` array — send directly to avoid double-wrapping.
   */
  const openAddOnRequired = useCallback(
    async (errorData: unknown): Promise<AddOnRequiredResponse> => {
      // Native billing: open AddonRequiredDialog instead of scio1 iframe
      if (useNativeBilling) {
        const data = errorData as Record<string, unknown>;
        useBillingDialogStore.getState().openDialog('addonRequired', {
          addonType: data?.addOnKey ?? data?.key,
          devices: data?.devices,
          items: data?.items,
        });
        return { close: true };
      }

      try {
        // Backend error extension may already contain an `items` array with addon details
        // (addOnKey, devices, etc.). If so, send it directly — don't double-wrap.
        // If it doesn't have items, wrap it as [errorData] for scio1's expected format.
        const data = errorData as Record<string, unknown>;
        const payload = Array.isArray(data?.items) ? errorData : { items: [errorData] };
        const response = await sendToScio1<AddOnRequiredResponse>('OPEN_ADD_REQUIRED', payload);
        return response ?? { close: true };
      } catch {
        return { close: true };
      }
    },
    [sendToScio1],
  );

  /**
   * Open the Subscription Plan modal in scio1.
   * Called when a feature requires a higher plan.
   */
  const openSubscriptionPlan = useCallback(
    async (data: {
      title?: string;
      subtitle?: string;
      action?: string;
      sizeModal?: string;
      params?: Record<string, unknown>;
    }): Promise<SubscriptionPlanResponse> => {
      // Native billing: open SubscriptionWizardDialog instead of scio1 iframe
      if (useNativeBilling) {
        useBillingDialogStore.getState().openDialog('subscriptionWizard', {
          planFeature: data.params?.planFeature,
          minPlanRequired: data.params?.minPlanRequired,
          subAccountId: data.params?.subAccountId,
          teamId: data.params?.teamId,
        });
        return {};
      }

      try {
        const response = await sendToScio1<SubscriptionPlanResponse>(
          'OPEN_SUBSCRIPTION_MODAL',
          data,
        );
        return response ?? {};
      } catch {
        return {};
      }
    },
    [sendToScio1],
  );

  /**
   * Open the Required Plan modal in scio1.
   * Called when an operation requires a specific plan (e.g., Engage).
   */
  const openRequiredPlan = useCallback(
    async (data: Record<string, unknown>): Promise<SubscriptionPlanResponse> => {
      // Native billing: open subscription wizard directly at the plan step
      if (useNativeBilling) {
        if (!data.minPlanRequired) return {};
        openUpgradeWizard(data.minPlanRequired as string);
        return {};
      }

      try {
        const response = await sendToScio1<SubscriptionPlanResponse>(
          'OPEN_SUBSCRIPTION_MODAL_REQUIRED_PLAN',
          data,
        );
        return response ?? {};
      } catch {
        return {};
      }
    },
    [sendToScio1],
  );

  /**
   * Handle a toggleFeature error with subscription modal flow.
   *
   * Returns a three-state result:
   * - `'retry'`     — user purchased addon, caller should retry the mutation
   * - `'handled'`   — user saw the modal and dismissed it, caller should stay silent
   * - `'unhandled'` — not a subscription/addon error, caller should show a generic error
   *
   * @param error — Apollo GraphQL error
   * @param devices — optional devices array for the addon modal (scio1 AericastAssignModal needs it).
   *   Format: `[{ _id, name }]`. The error extension often lacks this, so callers pass it in.
   */
  const handleToggleError = useCallback(
    async (
      error: unknown,
      devices?: Array<{ _id: string; name: string }>,
    ): Promise<'retry' | 'handled' | 'unhandled'> => {
      const ext = getGraphQLErrorExtension(error);
      if (!ext) return 'unhandled';

      // Backend returns numeric code from DEVICE_CODE_STATUS in the error extension.
      // Check both the extension's numeric code and the GraphQL error code string as fallback.
      const numericCode = (ext as Record<string, unknown>).code as number | undefined;
      const gqlCode = getGraphQLErrorCode(error);

      if (numericCode === DEVICE_CODE_STATUS.REQUIRED_PLAN || gqlCode === 'REQUIRED_PLAN') {
        // Plan upgrade needed — open the "Required Plan" modal in scio1 (engagePlanConfirmModal).
        // This shows a dialog with plan info, asset list, and "Update Subscription" / "Cancel" buttons,
        // matching scio1/scio2 behavior. Uses OPEN_SUBSCRIPTION_MODAL_REQUIRED_PLAN event (not the
        // generic OPEN_SUBSCRIPTION_MODAL which skips the confirmation dialog and shows the full plan page).
        const response = await openRequiredPlan(ext as Record<string, unknown>);

        if (response?.type === 'goToSub') {
          navigate('/app/s/subscription-plan');
        }
        return 'handled'; // Plan changes require page reload, don't retry
      }

      if (
        numericCode === DEVICE_CODE_STATUS.ADDON_ENABLE_TRIAL_NOT_ENOUGH ||
        numericCode === DEVICE_CODE_STATUS.AERICAST_PAID_NOT_ENOUGH ||
        numericCode === DEVICE_CODE_STATUS.ADDON_NOT_ENOUGH ||
        numericCode === DEVICE_CODE_STATUS.ADDON_TRIAL_NOT_ENOUGH ||
        numericCode === DEVICE_CODE_STATUS.AERICAST_TRIAL ||
        gqlCode === 'ADDON_ENABLE_TRIAL_NOT_ENOUGH' ||
        gqlCode === 'ADDON_ENABLE_PAID_NOT_ENOUGH'
      ) {
        // Add-on license limit — open add-on required modal
        // Merge caller-provided devices into ext (error extension often lacks device info)
        const extWithDevices = devices ? { ...ext, devices } : ext;
        const response = await openAddOnRequired(extWithDevices);

        if (response.status === true) {
          return 'retry'; // User purchased — retry toggle
        }
        if (response.type === 'goToSub') {
          navigate('/app/s/subscription-plan');
        }
        return 'handled'; // User dismissed or navigated away
      }

      return 'unhandled';
    },
    [navigate, openRequiredPlan, openAddOnRequired],
  );

  return {
    openAddOnRequired,
    openSubscriptionPlan,
    openRequiredPlan,
    handleToggleError,
  };
}
